/**
 * Quill image format that works with a PRIVATE Supabase `files` bucket.
 *
 * Images are stored in the document delta as PUBLIC files-bucket URLs
 * (`.../object/public/files/...`). That string is the stable identity the save
 * path and the image-removal cleanup depend on. A public URL 400s once the
 * bucket is private, so we override Quill's image blot to:
 *   • DISPLAY the image via a short-lived SIGNED URL (set asynchronously), and
 *   • SERIALIZE the stable public URL back into the delta (so saves never
 *     persist an expiring token and the removal-cleanup keeps matching).
 *
 * Registering once affects every Quill instance, so it covers both the doctor
 * editor (EditableDocumentPage) and the medstudent clinic editor (DocumentBody),
 * in read AND edit mode. Import this module for its side effect before any
 * editor is created.
 */
import Quill from "quill";
import { toSignedUrl } from "./signedUrls";

const ImageBlot = Quill.import("formats/image");
// Where we stash the canonical (stable) URL, separate from the displayed src.
const STORAGE_ATTR = "data-storage-url";
const isPublicStorageUrl = (v) =>
  typeof v === "string" && v.includes("/storage/v1/object/public/");

class SignedImage extends ImageBlot {
  static create(value) {
    const node = super.create(value);
    if (typeof value === "string") {
      node.setAttribute(STORAGE_ATTR, value);
      if (isPublicStorageUrl(value)) {
        // Swap only the DISPLAYED src to a signed URL. value() still returns
        // the stored public URL, so Quill keeps serializing that to the delta.
        // Long expiry (8h) so images survive a full editing session; a reload
        // always re-signs since the delta keeps the public URL. .catch so a
        // signing failure can never reject into the editor.
        toSignedUrl(value, 8 * 3600)
          .then((signed) => {
            if (signed && signed !== value) node.setAttribute("src", signed);
          })
          .catch(() => {});
      }
    }
    return node;
  }

  static value(domNode) {
    return domNode.getAttribute(STORAGE_ATTR) || domNode.getAttribute("src");
  }
}

Quill.register("formats/image", SignedImage, true);

export default SignedImage;
